import { createContext, useContext, useEffect, useState } from 'react';

/** What UnsavedChangesGuard keeps for the editors under it. */
export interface Guard {
  /** Record a dirty editor and whether page navigation would destroy its draft. */
  mark: (editor: symbol, changed: boolean, navigation: boolean) => void;
  /** Ask "Discard your changes?"; `leave` runs on Discard. */
  ask: (leave: () => void) => void;
}

export const GuardContext = createContext<Guard | null>(null);

function useGuard(): Guard {
  const guard = useContext(GuardContext);
  if (!guard) throw new Error('An editor that can hold unsaved changes needs an UnsavedChangesGuard above it');
  return guard;
}

/**
 * An editor's part in the guard (UnsavedChanges.tsx). `changed`: the form
 * differs from what was loaded. `confirmLeave` asks before leaving when there
 * are changes (Cancel); `leaveWithoutAsking` is for leaving once the edit is
 * saved or its item is gone. Global drafts that survive navigation opt out of
 * route blocking with navigation:false, while keeping unload and explicit-leave protection.
 */
export function useUnsavedChanges(changed: boolean, options: { navigation?: boolean } = {}) {
  const guard = useGuard();
  const [editor] = useState(() => Symbol('editor'));
  const navigation = options.navigation ?? true;
  useEffect(() => guard.mark(editor, changed, navigation), [guard, editor, changed, navigation]);
  useEffect(() => () => guard.mark(editor, false, navigation), [guard, editor, navigation]);
  return {
    confirmLeave: (leave: () => void) => (changed ? guard.ask(leave) : leave()),
    leaveWithoutAsking: (leave: () => void) => {
      guard.mark(editor, false, navigation);
      leave();
    },
  };
}
